<div class="mb-16">
  <!-- Header -->
  <div class="bg-[var(--tertiary)] py-12 mb-8">
    <div class="container-padding mx-auto max-w-screen-desktop">
      <h1 class="uppercase text-[var(--beige)] text-[40px]/[40px] font-normal mobile-m:text-left text-center">
        {{ 'payout.title' | translate }}
      </h1>
    </div>
  </div>

  <!-- Loading skeleton -->
  <div *ngIf="isLoadingInfo" class="mx-auto max-w-screen-desktop container-padding pt-0 pb-20">
    <div class="grid grid-cols-1 mobile-m:grid-cols-2 gap-4 mb-6">
      <app-skeleton dimensions="h-28 w-full rounded-2xl"></app-skeleton>
      <app-skeleton dimensions="h-28 w-full rounded-2xl"></app-skeleton>
    </div>
    <app-skeleton dimensions="h-14 w-48 rounded-xl mb-6"></app-skeleton>
    <app-skeleton dimensions="h-40 w-full rounded-2xl"></app-skeleton>
  </div>

  <!-- Error state -->
  <div *ngIf="errorInfo && !isLoadingInfo" class="mx-auto max-w-screen-desktop container-padding pt-0 pb-20">
    <div class="text-center py-12">
      <p class="text-red-600 mb-4">{{ errorInfo }}</p>
      <app-button [text]="'payout.retry' | translate" [function]="loadPayoutInfo.bind(this)"></app-button>
    </div>
  </div>

  <!-- Main content -->
  <div *ngIf="!isLoadingInfo && !errorInfo && payoutInfo" class="mx-auto max-w-screen-desktop container-padding pt-0 pb-20">

    <!-- My Codes section (always visible, above tabs) -->
    <div class="mb-8">
      <h2 class="text-sm font-semibold uppercase tracking-widest text-[var(--dark-grey)] mb-3">{{ 'payout.myCodes' | translate }}</h2>

      <div *ngIf="isLoadingCodes" class="flex gap-3 overflow-x-auto pb-2">
        <app-skeleton dimensions="h-40 w-64 rounded-2xl shrink-0"></app-skeleton>
        <app-skeleton dimensions="h-40 w-64 rounded-2xl shrink-0"></app-skeleton>
      </div>

      <p *ngIf="!isLoadingCodes && activeCodes.length === 0" class="text-sm text-[var(--dark-grey)]">
        {{ 'payout.noCodes' | translate }}
      </p>

      <div *ngIf="!isLoadingCodes && activeCodes.length > 0" class="flex gap-4 overflow-x-auto pb-2">
        <div *ngFor="let code of activeCodes"
             class="shrink-0 w-72 rounded-2xl bg-[var(--tertiary)] text-white p-5 flex flex-col gap-4 relative overflow-hidden">
          <!-- Decorative circles -->
          <div class="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-white opacity-10"></div>
          <div class="absolute right-4 -bottom-10 w-36 h-36 rounded-full bg-white opacity-5"></div>

          <!-- Code -->
          <div>
            <p class="text-[10px] uppercase tracking-[0.2em] opacity-60 mb-1">{{ 'payout.code' | translate }}</p>
            <p class="text-3xl font-bold tracking-[0.15em]">{{ code.code }}</p>
          </div>

          <!-- Divider -->
          <div class="border-t border-white opacity-20"></div>

          <!-- Stats row — each stat clearly separated -->
          <div class="grid grid-cols-3 gap-2">
            <div>
              <p class="text-[10px] uppercase tracking-wider opacity-60 mb-1">{{ 'payout.discount' | translate }}</p>
              <p class="text-xl font-bold">{{ code.percentage }}%</p>
            </div>
            <div>
              <p class="text-[10px] uppercase tracking-wider opacity-60 mb-1">{{ 'payout.commission' | translate }}</p>
              <p class="text-xl font-bold">{{ getCommissionLabel(code) }}</p>
            </div>
            <div class="text-right">
              <p class="text-[10px] uppercase tracking-wider opacity-60 mb-1">{{ 'payout.uses' | translate }}</p>
              <p class="text-base font-semibold">{{ code.uses }}<span class="opacity-50 text-sm">/{{ code.maxUses }}</span></p>
            </div>
          </div>

          <!-- Expiration -->
          <p class="text-[11px] opacity-40">{{ 'payout.expiration' | translate }}: {{ code.expirationDate | date:'dd/MM/yyyy' }}</p>
        </div>
      </div>
    </div>

    <!-- Balance cards -->
    <div class="grid grid-cols-1 mobile-m:grid-cols-2 gap-4 mb-6">
      <div class="p-6 rounded-2xl border-4 border-[var(--tertiary)] bg-white">
        <p class="text-sm text-[var(--dark-grey)] font-medium mb-1">{{ 'payout.availableBalance' | translate }}</p>
        <p class="text-3xl font-bold text-[var(--tertiary)]">{{ payoutInfo.availableBalance | currency }}</p>
      </div>
      <div class="p-6 rounded-2xl border-4 border-[var(--grey)] bg-white">
        <p class="text-sm text-[var(--dark-grey)] font-medium mb-1">{{ 'payout.minThreshold' | translate }}</p>
        <p class="text-3xl font-bold text-[var(--black)]">{{ payoutInfo.minPayoutThreshold | currency }}</p>
        <p *ngIf="payoutInfo.remainingToThreshold > 0" class="text-xs text-[var(--dark-grey)] mt-1">
          {{ 'payout.remainingToThreshold' | translate }} {{ payoutInfo.remainingToThreshold | currency }}
        </p>
      </div>
    </div>

    <!-- Request payout button -->
    <div class="mb-8 flex flex-col mobile-m:flex-row mobile-m:items-center gap-4 mobile-m:gap-3">
      <app-button
        [text]="'payout.requestButton' | translate"
        [disabled]="!payoutInfo.isEligible || payoutInfo.hasPendingRequest || isSubmitting"
        [isLoading]="isSubmitting"
        [function]="submitPayoutRequest.bind(this)"
      ></app-button>
      <span *ngIf="!payoutInfo.isEligible && !payoutInfo.hasPendingRequest" class="text-xs text-[var(--dark-grey)]">
        {{ 'payout.reachThreshold' | translate }}
      </span>
      <span *ngIf="payoutInfo.hasPendingRequest" class="text-xs text-[var(--dark-orange)]">
        {{ 'payout.hasPending' | translate }}
      </span>
    </div>

    <!-- Pending request alert -->
    <div *ngIf="payoutInfo.hasPendingRequest && payoutInfo.pendingRequest" class="mb-6 p-4 rounded-2xl border-2 border-[var(--yellow)] bg-[#FFF9ED]">
      <div class="flex items-center gap-2 mb-1">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[var(--yellow)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="font-semibold text-[var(--black)] text-sm">{{ 'payout.pendingRequest' | translate }}</span>
      </div>
      <p class="text-sm text-[var(--dark-grey)]">
        {{ 'payout.requestFrom' | translate }} <strong class="text-[var(--black)]">{{ payoutInfo.pendingRequest.requestedAmount | currency }}</strong>
        {{ 'payout.date' | translate | lowercase }} {{ payoutInfo.pendingRequest.createdAt | date:'dd/MM/yyyy' }} {{ 'payout.waitingApproval' | translate }}
      </p>
    </div>

    <!-- Success/Error messages -->
    <div *ngIf="successMessage" class="mb-4 p-4 rounded-xl border-2 border-green-300 bg-green-50 text-green-800 text-sm">
      {{ successMessage }}
    </div>
    <div *ngIf="errorSubmit" class="mb-4 p-4 rounded-xl border-2 border-red-300 bg-red-50 text-red-800 text-sm">
      {{ errorSubmit }}
    </div>

    <!-- History tabs -->
    <div class="mt-4">
      <div class="flex gap-1 border-b-2 border-[var(--grey)] mb-4 overflow-x-auto scrollbar-hide">
        <button
          (click)="setActiveTab('commissions')"
          class="px-4 py-3 text-sm font-medium whitespace-nowrap transition-all border-b-3 -mb-[2px]"
          [ngClass]="activeTab === 'commissions' ? 'border-[var(--tertiary)] text-[var(--tertiary)]' : 'border-transparent text-[var(--dark-grey)] hover:text-[var(--black)]'"
        >
          {{ 'payout.commissions' | translate }}
        </button>
        <button
          (click)="setActiveTab('requests')"
          class="px-4 py-3 text-sm font-medium whitespace-nowrap transition-all border-b-3 -mb-[2px]"
          [ngClass]="activeTab === 'requests' ? 'border-[var(--tertiary)] text-[var(--tertiary)]' : 'border-transparent text-[var(--dark-grey)] hover:text-[var(--black)]'"
        >
          {{ 'payout.requests' | translate }}
        </button>
        <button
          (click)="setActiveTab('payouts')"
          class="px-4 py-3 text-sm font-medium whitespace-nowrap transition-all border-b-3 -mb-[2px]"
          [ngClass]="activeTab === 'payouts' ? 'border-[var(--tertiary)] text-[var(--tertiary)]' : 'border-transparent text-[var(--dark-grey)] hover:text-[var(--black)]'"
        >
          {{ 'payout.payoutsReceived' | translate }}
        </button>
      </div>

      <div class="min-h-[200px]">

        <!-- Tab: Commissions -->
        <div *ngIf="activeTab === 'commissions'">
          <div *ngIf="isLoadingCommissions" class="space-y-2">
            <app-skeleton dimensions="h-14 w-full rounded-xl"></app-skeleton>
            <app-skeleton dimensions="h-14 w-full rounded-xl"></app-skeleton>
            <app-skeleton dimensions="h-14 w-full rounded-xl"></app-skeleton>
          </div>

          <p *ngIf="!isLoadingCommissions && commissions.length === 0" class="text-[var(--dark-grey)] text-center py-8 text-sm">
            {{ 'payout.noCommissions' | translate }}
          </p>

          <div *ngIf="!isLoadingCommissions && commissions.length > 0" class="space-y-2">
            <div *ngFor="let commission of commissions"
                 class="flex items-center justify-between p-4 rounded-xl border-2 border-[var(--grey)] hover:border-[var(--dark-grey)] transition-colors bg-white">
              <div class="flex items-center gap-3 min-w-0">
                <span class="px-2.5 py-1 text-xs font-medium rounded-full shrink-0" [ngClass]="getStatusColor(commission.status)">
                  {{ getStatusLabel(commission.status) }}
                </span>
                <div class="min-w-0">
                  <span *ngIf="commission.discountCode" class="text-xs font-bold text-[var(--tertiary)] tracking-wider block">
                    {{ commission.discountCode.code }}
                  </span>
                  <span class="text-sm text-[var(--dark-grey)]">{{ commission.createdAt | date:'dd/MM/yyyy' }}</span>
                </div>
              </div>
              <span class="font-bold text-sm text-[var(--black)] shrink-0 ml-3">{{ commission.amountEarned | currency }}</span>
            </div>
          </div>
        </div>

        <!-- Tab: Payout Requests -->
        <div *ngIf="activeTab === 'requests'">
          <div *ngIf="isLoadingRequests" class="space-y-2">
            <app-skeleton dimensions="h-14 w-full rounded-xl"></app-skeleton>
            <app-skeleton dimensions="h-14 w-full rounded-xl"></app-skeleton>
          </div>

          <p *ngIf="!isLoadingRequests && payoutRequests.length === 0" class="text-[var(--dark-grey)] text-center py-8 text-sm">
            {{ 'payout.noRequests' | translate }}
          </p>

          <div *ngIf="!isLoadingRequests && payoutRequests.length > 0" class="space-y-2">
            <div *ngFor="let request of payoutRequests"
                 class="flex items-center justify-between p-4 rounded-xl border-2 border-[var(--grey)] hover:border-[var(--dark-grey)] transition-colors bg-white">
              <div class="flex items-center gap-3">
                <span class="px-2.5 py-1 text-xs font-medium rounded-full" [ngClass]="getStatusColor(request.status)">
                  {{ getStatusLabel(request.status) }}
                </span>
                <div>
                  <span class="text-xs font-bold text-[var(--tertiary)] tracking-wider block">{{ request.reference }}</span>
                  <span class="text-sm text-[var(--dark-grey)]">{{ request.createdAt | date:'dd/MM/yyyy' }}</span>
                </div>
              </div>
              <span class="font-bold text-sm text-[var(--black)]">{{ request.requestedAmount | currency }}</span>
            </div>
          </div>
        </div>

        <!-- Tab: Payouts received -->
        <div *ngIf="activeTab === 'payouts'">
          <div *ngIf="isLoadingPayouts" class="space-y-2">
            <app-skeleton dimensions="h-14 w-full rounded-xl"></app-skeleton>
            <app-skeleton dimensions="h-14 w-full rounded-xl"></app-skeleton>
          </div>

          <p *ngIf="!isLoadingPayouts && payouts.length === 0" class="text-[var(--dark-grey)] text-center py-8 text-sm">
            {{ 'payout.noPayouts' | translate }}
          </p>

          <div *ngIf="!isLoadingPayouts && payouts.length > 0" class="space-y-2">
            <div *ngFor="let payout of payouts"
                 class="flex items-center justify-between p-4 rounded-xl border-2 border-[var(--grey)] hover:border-[var(--dark-grey)] transition-colors bg-white">
              <div class="flex items-center gap-3">
                <span class="px-2.5 py-1 text-xs font-medium rounded-full" [ngClass]="getOriginColor(payout.originType)">
                  {{ getOriginLabel(payout.originType) }}
                </span>
                <div>
                  <span class="text-xs font-bold text-[var(--tertiary)] tracking-wider block">{{ payout.reference }}</span>
                  <span class="text-sm text-[var(--dark-grey)]">{{ payout.createdAt | date:'dd/MM/yyyy' }}</span>
                </div>
              </div>
              <span class="font-bold text-sm text-[var(--black)]">{{ payout.totalAmount | currency }}</span>
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>
</div>
